import {
  DEFAULT_METRIC_COLUMN_WIDTH,
  METRIC_HEADER_FONT,
  MIN_COLUMN_WIDTH,
  DEFAULT_PADDING,
} from "../constants"
import { calculateOptimalTextWidth } from "./text-measurement"
import { measureRenderNode } from "./dom-measurement"

/**
 * Gets all column IDs except the label column
 * @param {Object} apiRef - DataGrid API reference
 * @returns {Array<string>} Array of column field names
 */
export function getNonLabelColumnIds(apiRef) {
  if (!apiRef.current) return []
  return apiRef.current.getAllColumns()
    .filter(col => col.field !== "label")
    .map(col => col.field)
}

/**
 * Resizes columns based on their header text width
 * @param {Object} apiRef - DataGrid API reference
 * @returns {Promise<void>}
 */
export async function resizeColumnsByHeaderNode(apiRef) {
  if (!apiRef.current) return

  const allColumns = apiRef.current.getAllColumns()
  const nonLabelColumns = allColumns.filter(col => col.field !== "label")

  const resizePromises = nonLabelColumns.map(async (column) => {
    let width

    if (column?.renderHeader) {
      // For custom header renderers, measure the actual rendered output
      const headerNode = column.renderHeader({ ...column, colDef: column })
      width = await measureRenderNode(headerNode, { nowrap: true })
    } else {
      // For simple text headers, use canvas-based measurement
      const headerText = column?.headerName || column?.field
      width = calculateOptimalTextWidth([headerText], METRIC_HEADER_FONT, {
        minWidth: MIN_COLUMN_WIDTH,
        maxWidth: Number.MAX_SAFE_INTEGER,
        defaultWidth: DEFAULT_METRIC_COLUMN_WIDTH,
        padding: DEFAULT_PADDING,
      })
    }

    apiRef.current.setColumnWidth(column.field, width + DEFAULT_PADDING)
  })

  await Promise.all(resizePromises)
}

/**
 * Collapses columns to their minimum width based on cell content
 * @param {Object} apiRef - DataGrid API reference
 */
export function collapseColumns(apiRef) {
  if (!apiRef.current) return

  // We don't want to shrink the label column
  const columnsToResize = getNonLabelColumnIds(apiRef)
  apiRef.current.autosizeColumns({
    columns: columnsToResize,
    includeHeaders: false,
    includeOutliers: false,
    expand: false,
  })
}
